import React, { useState, useEffect, useContext } from "react";
import {
  Typography,
  Box,
  Backdrop,
  CircularProgress,
  TextField,
  InputAdornment,
  Button,
} from "@mui/material";
import axios from "axios";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import { ConditionItem } from "../../components/condition/ConditionItem";
import { Add } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import { ThemeContext } from "../../context/ThemeContext";
import { getColors } from "../../themes/theme";
import { AddConditionDialog } from "../../components/condition/AddConditionDialog";
import { useSnackbar } from "notistack";

const ConditionPage = () => {
  const { token } = useAuthContext();
  const [loading, setLoading] = useState(true); // Loading state for fetching data
  const [conditions, setConditions] = useState([]); // State to store exercises fetched from backend
  const [searchQuery, setSearchQuery] = useState(""); // State for search query
  const [filteredConditions, setFilteredConditions] = useState([]); // State for filtered exercises
  const { darkMode } = useContext(ThemeContext);
  const [openAddDialog, setOpenAddDialog] = useState(false); // New state for confirmation dialog
  const { enqueueSnackbar } = useSnackbar();

  const updateCondition = (updatedCondition) => {
    setConditions((prevConditions) => {
      let updatedConditions;

      if (updatedCondition.deleted) {
        // Remove the deleted exercise from the state
        updatedConditions = prevConditions.filter(
          (condition) => condition.id !== updatedCondition.id
        );
      } else if (
        !prevConditions.some(
          (condition) => condition.id === updatedCondition.id
        )
      ) {
        // Add the new exercise if it doesn't already exist
        updatedConditions = [...prevConditions, updatedCondition];
      } else {
        // Update the existing exercise
        updatedConditions = prevConditions.map((condition) =>
          condition.id === updatedCondition.id ? updatedCondition : condition
        );
      }

      // Sort exercises alphabetically by name
      return updatedConditions.sort((a, b) =>
        a.acondition.localeCompare(b.acondition, undefined, {
          sensitivity: "base",
        })
      );
    });
  };

  useEffect(() => {
    // Fetch exercises from the backend
    if (!token) return;
    const fetchConditions = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/condition`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setConditions(response.data);
        setFilteredConditions(response.data); // Initialize filtered exercises
      } catch (err) {
        console.error("Error fetching conditions:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchConditions();
  }, [token]);

  useEffect(() => {
    // Filter exercises based on the search query
    setFilteredConditions(
      conditions.filter((condition) => {
        const fullString = `${condition.acondition} (${condition.area})`;
        return fullString.toLowerCase().includes(searchQuery.toLowerCase());
      })
    );
  }, [searchQuery, conditions]);

  const handleAddCondition = async (data) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/condition`,
        {
          ...data,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      updateCondition(response.data); // Update the parent state
      enqueueSnackbar("Successfully Added!", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to add condition!", { variant: "error" });
    }
  };

  const handleAddClicked = () => {
    setOpenAddDialog(true);
  };

  const handleCancelAdd = () => {
    setOpenAddDialog(false);
  };

  return (
    <>
      <Box
        sx={{
          pt: "60px",
          pl: 5,
          pr: 10,
        }}
      >
        <Backdrop sx={{ zIndex: 1400 }} open={loading}>
          <CircularProgress size={80} />
        </Backdrop>
        {!loading && (
          <>
            <Typography
              sx={{
                fontFamily: "Poppins",
                fontSize: "28px",
                color: darkMode
                  ? getColors.drawerTextDark
                  : getColors.thumbnailTextLight,
                fontWeight: 600,
                textAlign: "left",
                mb: 5,
              }}
            >
              Base Condition Management
            </Typography>
            <Box display="flex" justifyContent="space-between" sx={{ mb: 3 }}>
              <TextField
                variant="filled"
                placeholder="Search conditions"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ mr: 0 }}>
                      <SearchIcon sx={{ mt: -2, ml: 1 }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                startIcon={<Add />}
                variant="contained"
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "18px",
                  fontWeight: 500,
                  px: 3,
                  py: 2,
                  background: darkMode ? "#2D2E30" : "#E6E6E6",
                  color: darkMode
                    ? getColors.primaryTextDark
                    : getColors.secondaryTextLight,
                  "&:hover": {
                    color: getColors.primaryTextDark,
                  },
                }}
                onClick={handleAddClicked}
              >
                Add Condition
              </Button>
            </Box>
            <Box
              display="grid"
              gridTemplateColumns="repeat(3, 1fr)"
              gap={4}
              rowGap={4}
            >
              {filteredConditions.map((condition, index) => (
                <ConditionItem
                  key={index}
                  {...condition}
                  onConditionUpdate={updateCondition}
                />
              ))}
            </Box>
          </>
        )}
      </Box>
      <AddConditionDialog
        open={openAddDialog}
        onClose={handleCancelAdd}
        onAdd={handleAddCondition}
      />
    </>
  );
};

export default ConditionPage;
